import React from 'react';

/**
 * Expand a Google Map to a full-screen overlay WITHOUT re-creating the map or
 * re-plotting: callers just toggle a CSS class on the map card. This hook owns
 * the side effects that a CSS resize needs —
 *   - trigger Google's `resize` so the tiles refill the larger canvas, then
 *     re-frame via the caller's `onRefit` (fit the existing markers);
 *   - Escape closes; the body scroll locks while open (same as the app's modals).
 * The map instance is reused as-is, so the user literally sees the same map.
 */
export function useFullscreenMap(
  mapInstanceRef: React.MutableRefObject<any>,
  onRefit?: () => void,
) {
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  // Keep the latest onRefit without re-running the resize effect every render.
  const onRefitRef = React.useRef(onRefit);
  onRefitRef.current = onRefit;
  const isFirstRun = React.useRef(true);

  const close = React.useCallback(() => setIsFullscreen(false), []);
  const toggle = React.useCallback(() => setIsFullscreen((value) => !value), []);

  // Resize + re-frame after the panel changes size (skip the initial mount so a
  // non-fullscreen map is never re-framed out from under the user).
  React.useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return undefined;
    }
    const map = mapInstanceRef.current;
    if (!map || !window.google?.maps?.event) return undefined;
    const timer = window.setTimeout(() => {
      window.google.maps.event.trigger(map, 'resize');
      onRefitRef.current?.();
    }, 60); // let the CSS transition settle before measuring
    return () => window.clearTimeout(timer);
  }, [isFullscreen, mapInstanceRef]);

  // Escape to close + lock body scroll while the overlay is up.
  React.useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [isFullscreen]);

  return { isFullscreen, toggle, close };
}
